import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { gql } from "graphql-request";
import { graphqlClient } from "@/lib/graphql";
import { useAuth } from "@/hooks/useAuth";
import type { User } from "@/types";

const UPDATE_NOTIFICATION_SETTINGS_MUTATION = gql`
  mutation UpdateNotificationSettings(
    $emailNotificationFrequency: String
    $browserNotificationsEnabled: Boolean
    $quietHoursEnabled: Boolean
    $quietHoursStart: Int
    $quietHoursEnd: Int
  ) {
    updateNotificationSettings(
      emailNotificationFrequency: $emailNotificationFrequency
      browserNotificationsEnabled: $browserNotificationsEnabled
      quietHoursEnabled: $quietHoursEnabled
      quietHoursStart: $quietHoursStart
      quietHoursEnd: $quietHoursEnd
    ) {
      user {
        id
        email
        displayName
        bio
        avatarUrl
        rating
        ratingsCount
        createdAt
        admin
        activeListingsCount
        latitude
        longitude
        city
        address
        defaultDelivery
        defaultPickup
        defaultPickupRadius
        emailNotificationFrequency
        browserNotificationsEnabled
        quietHoursEnabled
        quietHoursStart
        quietHoursEnd
      }
      errors
    }
  }
`;

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: `${i.toString().padStart(2, "0")}:00`,
}));

export function NotificationSettingsPage() {
  const { t } = useTranslation();
  const { user, updateUser } = useAuth();

  const [emailFrequency, setEmailFrequency] = useState("daily");
  const [browserEnabled, setBrowserEnabled] = useState(true);
  const [quietHoursEnabled, setQuietHoursEnabled] = useState(true);
  const [quietHoursStart, setQuietHoursStart] = useState(22);
  const [quietHoursEnd, setQuietHoursEnd] = useState(8);
  const [errors, setErrors] = useState<string[]>([]);
  const [success, setSuccess] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [browserPermission, setBrowserPermission] = useState<
    NotificationPermission | "unsupported"
  >("default");

  useEffect(() => {
    if (user && !initialized) {
      setEmailFrequency(user.emailNotificationFrequency ?? "daily");
      setBrowserEnabled(user.browserNotificationsEnabled ?? true);
      setQuietHoursEnabled(user.quietHoursEnabled ?? true);
      setQuietHoursStart(user.quietHoursStart ?? 22);
      setQuietHoursEnd(user.quietHoursEnd ?? 8);
      setInitialized(true);
    }
  }, [user, initialized]);

  useEffect(() => {
    if ("Notification" in window) {
      setBrowserPermission(Notification.permission);
    } else {
      setBrowserPermission("unsupported");
    }
  }, []);

  const requestBrowserPermission = async () => {
    if ("Notification" in window) {
      const permission = await Notification.requestPermission();
      setBrowserPermission(permission);
      if (permission === "granted") {
        setBrowserEnabled(true);
      }
    }
  };

  const updateMutation = useMutation({
    mutationFn: (vars: {
      emailNotificationFrequency?: string;
      browserNotificationsEnabled?: boolean;
      quietHoursEnabled?: boolean;
      quietHoursStart?: number;
      quietHoursEnd?: number;
    }) =>
      graphqlClient.request<{
        updateNotificationSettings: { user: User | null; errors: string[] };
      }>(UPDATE_NOTIFICATION_SETTINGS_MUTATION, vars),
    onSuccess: (data) => {
      if (data.updateNotificationSettings.user) {
        updateUser(data.updateNotificationSettings.user);
        setErrors([]);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        setErrors(data.updateNotificationSettings.errors);
        setSuccess(false);
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors([]);
    setSuccess(false);

    updateMutation.mutate({
      emailNotificationFrequency: emailFrequency,
      browserNotificationsEnabled: browserEnabled,
      quietHoursEnabled: quietHoursEnabled,
      quietHoursStart: quietHoursStart,
      quietHoursEnd: quietHoursEnd,
    });
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-4">
        <Link
          to="/profile"
          className="text-sm text-primary-800 hover:text-primary-900"
        >
          ← {t("settings.title")}
        </Link>
      </div>
      <h1 className="mb-8 text-2xl font-bold text-gray-900">
        {t("settings.notifications.title")}
      </h1>

      {errors.length > 0 && (
        <div className="mb-6 rounded-lg bg-red-50 p-3 text-sm text-red-700">
          {errors.map((err, i) => (
            <p key={i}>{err}</p>
          ))}
        </div>
      )}

      {success && (
        <div className="mb-6 rounded-lg bg-green-50 p-3 text-sm text-green-700">
          {t("settings.notifications.update_success")}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Email notifications section */}
          <div className="rounded-lg border border-gray-200 bg-white">
            <div className="border-b border-gray-200 px-6 py-5">
              <h2 className="text-base font-semibold text-gray-900">
                {t("settings.notifications.email_section")}
              </h2>
              <p className="mt-1 text-xs text-gray-500">
                {t("settings.notifications.email_section_hint")}
              </p>
            </div>

            <div className="px-6 py-5">
              <label
                htmlFor="emailFrequency"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                {t("settings.notifications.email_frequency")}
              </label>
              <select
                id="emailFrequency"
                value={emailFrequency}
                onChange={(e) => setEmailFrequency(e.target.value)}
                className="input w-full sm:w-64"
              >
                <option value="instant">
                  {t("settings.notifications.email_frequency_instant")}
                </option>
                <option value="daily">
                  {t("settings.notifications.email_frequency_daily")}
                </option>
                <option value="weekly">
                  {t("settings.notifications.email_frequency_weekly")}
                </option>
                <option value="never">
                  {t("settings.notifications.email_frequency_never")}
                </option>
              </select>
            </div>
          </div>

          {/* Browser notifications section */}
          <div className="rounded-lg border border-gray-200 bg-white">
            <div className="border-b border-gray-200 px-6 py-5">
              <h2 className="text-base font-semibold text-gray-900">
                {t("settings.notifications.browser_section")}
              </h2>
              <p className="mt-1 text-xs text-gray-500">
                {t("settings.notifications.browser_section_hint")}
              </p>
            </div>

            <div className="px-6 py-5">
              <div className="flex items-center gap-3">
                <label className="relative inline-flex cursor-pointer items-center">
                  <input
                    type="checkbox"
                    checked={browserEnabled}
                    onChange={(e) => setBrowserEnabled(e.target.checked)}
                    className="peer sr-only"
                  />
                  <div className="h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary-800 peer-checked:after:translate-x-full peer-checked:after:border-white" />
                </label>
                <span className="text-sm text-gray-700">
                  {t("settings.notifications.browser_enabled")}
                </span>
              </div>

              {browserEnabled && browserPermission !== "unsupported" && (
                <div className="mt-3 ml-14">
                  {browserPermission === "granted" && (
                    <span className="inline-flex items-center gap-1 text-sm text-green-600">
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      {t("settings.notifications.browser_permission_granted")}
                    </span>
                  )}
                  {browserPermission === "denied" && (
                    <span className="text-sm text-red-500">
                      {t("settings.notifications.browser_permission_denied")}
                    </span>
                  )}
                  {browserPermission === "default" && (
                    <button
                      type="button"
                      onClick={requestBrowserPermission}
                      className="text-sm font-medium text-primary-800 hover:text-primary-900"
                    >
                      {t("settings.notifications.browser_permission_request")}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Quiet hours section */}
          <div className="rounded-lg border border-gray-200 bg-white">
            <div className="border-b border-gray-200 px-6 py-5">
              <h2 className="text-base font-semibold text-gray-900">
                {t("settings.notifications.quiet_hours_section")}
              </h2>
              <p className="mt-1 text-xs text-gray-500">
                {t("settings.notifications.quiet_hours_section_hint")}
              </p>
            </div>

            <div className="space-y-4 px-6 py-5">
              <div className="flex items-center gap-3">
                <label className="relative inline-flex cursor-pointer items-center">
                  <input
                    type="checkbox"
                    checked={quietHoursEnabled}
                    onChange={(e) => setQuietHoursEnabled(e.target.checked)}
                    className="peer sr-only"
                  />
                  <div className="h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary-800 peer-checked:after:translate-x-full peer-checked:after:border-white" />
                </label>
                <span className="text-sm text-gray-700">
                  {t("settings.notifications.quiet_hours_enabled")}
                </span>
              </div>

              {quietHoursEnabled && (
                <div className="ml-14 flex flex-wrap items-center gap-4">
                  <div>
                    <label
                      htmlFor="quietStart"
                      className="mb-1 block text-sm text-gray-600"
                    >
                      {t("settings.notifications.quiet_hours_start")}
                    </label>
                    <select
                      id="quietStart"
                      value={quietHoursStart}
                      onChange={(e) =>
                        setQuietHoursStart(parseInt(e.target.value, 10))
                      }
                      className="input w-28"
                    >
                      {HOUR_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor="quietEnd"
                      className="mb-1 block text-sm text-gray-600"
                    >
                      {t("settings.notifications.quiet_hours_end")}
                    </label>
                    <select
                      id="quietEnd"
                      value={quietHoursEnd}
                      onChange={(e) =>
                        setQuietHoursEnd(parseInt(e.target.value, 10))
                      }
                      className="input w-28"
                    >
                      {HOUR_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Save button */}
        <div className="mt-6 flex justify-end">
          <button
            type="submit"
            disabled={updateMutation.isPending}
            className="btn-primary"
          >
            {updateMutation.isPending
              ? t("settings.notifications.updating")
              : t("settings.notifications.update_button")}
          </button>
        </div>
      </form>
    </div>
  );
}
